import { useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Loader2, Globe, Bot, BarChart3, Workflow, Users, CheckCircle2, ArrowRight, User } from "lucide-react";
import { BackButton } from "@/components/layout/BackButton";

interface CloneStatus {
  status: "idle" | "analyzing" | "cloning" | "injecting" | "training" | "ready" | "deployed";
  progress: number;
  message: string;
  previewUrl?: string;
  deployedUrl?: string;
}

const services = [
  { id: "chatbot", label: "Live Chat", icon: Bot, description: "24/7 customer support widget" },
  { id: "analytics", label: "Analytics", icon: BarChart3, description: "Visitor tracking & insights" },
  { id: "automation", label: "Automation", icon: Workflow, description: "Workflow & CRM integration" },
  { id: "crm", label: "Lead Capture", icon: Users, description: "Email capture & lead forms" },
];

const steps = [
  { id: "analyzing", label: "Analyzing Site", message: "Scanning your website structure..." },
  { id: "cloning", label: "Cloning Content", message: "Downloading pages and assets..." },
  { id: "injecting", label: "Adding Services", message: "Installing chat widget and tools..." },
  { id: "training", label: "Configuring", message: "Setting up smart responses..." },
  { id: "ready", label: "Ready", message: "Your upgraded site is ready!" },
];

const specialists = [
  { name: "Sarah Chen", role: "Senior Web Engineer", avatar: "👩‍💻" },
  { name: "Marcus Williams", role: "Integration Specialist", avatar: "👨‍💻" },
  { name: "Priya Patel", role: "Full-Stack Developer", avatar: "👩‍💼" },
  { name: "James Rodriguez", role: "DevOps Engineer", avatar: "👨‍💼" },
  { name: "Emma Thompson", role: "UI/UX Developer", avatar: "👩‍🎨" },
];

export default function SiteCloner() {
  const [, navigate] = useLocation();
  const [url, setUrl] = useState("");
  const [selectedServices, setSelectedServices] = useState<string[]>(["chatbot"]);
  const [cloneStatus, setCloneStatus] = useState<CloneStatus>({
    status: "idle",
    progress: 0,
    message: "Enter your website URL to get started",
  });
  const [cloneResult, setCloneResult] = useState<any>(null);
  const [assignedSpecialist, setAssignedSpecialist] = useState<{name: string, role: string, avatar: string} | null>(null);

  const toggleService = (id: string) => {
    setSelectedServices(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const startCloning = async () => {
    if (!url) return;

    // Assign a specialist
    const specialist = specialists[Math.floor(Math.random() * specialists.length)];
    setAssignedSpecialist(specialist);

    setCloneStatus({ status: "analyzing", progress: 10, message: "Analyzing your website..." });

    try {
      const response = await fetch("/api/site-clone/clone", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url,
          services: {
            chatbot: selectedServices.includes("chatbot"),
            analytics: selectedServices.includes("analytics"),
            automation: selectedServices.includes("automation"),
            crm: selectedServices.includes("crm"),
          },
        }),
      });

      const data = await response.json();

      if (data.success) {
        setCloneResult(data);
        setCloneStatus({
          status: "ready",
          progress: 100,
          message: "Your upgraded site is ready!",
          previewUrl: data.previewUrl,
        });
      } else {
        setCloneStatus({ status: "idle", progress: 0, message: data.error || "Failed to clone" });
      }
    } catch (error) {
      setCloneStatus({ status: "idle", progress: 0, message: "Error: " + (error as Error).message });
    }
  };

  const deploySite = async () => {
    if (!cloneResult?.cloneId) return;

    setCloneStatus({ status: "deployed", progress: 100, message: "Deploying to production..." });

    try {
      const response = await fetch(`/api/site-clone/deploy/${cloneResult.cloneId}`, {
        method: "POST",
      });
      const data = await response.json();

      if (data.success) {
        setCloneStatus({
          status: "deployed",
          progress: 100,
          message: "Your site is live!",
          deployedUrl: data.deployedUrl,
        });
      }
    } catch (error) {
      console.error("Deploy error:", error);
    }
  };

  const isCloning = ["analyzing", "cloning", "injecting", "training"].includes(cloneStatus.status);

  return (
    <div className="min-h-screen bg-background pt-8 pb-12 px-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <BackButton fallback="/" />
        </div>

        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary to-purple-400">
            Site Upgrader
          </h1>
          <p className="text-muted-foreground text-lg">
            Clone your existing website and add smart conversion features
          </p>
        </div>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Website URL
            </CardTitle>
            <CardDescription>
              Enter the URL of your current website
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Input
                placeholder="https://your-website.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="flex-1"
                disabled={isCloning}
              />
              <Button
                onClick={startCloning}
                disabled={!url || isCloning}
                className="bg-primary"
              >
                {isCloning ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Cloning...
                  </>
                ) : (
                  <>
                    Clone & Upgrade
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {isCloning && (
          <Card className="mb-8 border-primary/20">
            <CardContent className="pt-6">
              {assignedSpecialist && (
                <div className="flex items-center justify-center gap-3 mb-6 p-4 bg-muted rounded-lg">
                  <span className="text-3xl">{assignedSpecialist.avatar}</span>
                  <div className="text-center">
                    <p className="font-medium">{assignedSpecialist.name}</p>
                    <p className="text-sm text-muted-foreground">{assignedSpecialist.role} · Working on your upgrade</p>
                  </div>
                </div>
              )}
              <div className="space-y-4">
                <Progress value={cloneStatus.progress} className="h-2" />
                <p className="text-center text-muted-foreground">{cloneStatus.message}</p>
                <div className="flex justify-center gap-2">
                  {steps.map((step, idx) => {
                    const isActive = cloneStatus.status === step.id;
                    const isDone = steps.findIndex(s => s.id === cloneStatus.status) > idx;
                    return (
                      <Badge
                        key={step.id}
                        variant={isActive ? "default" : isDone ? "secondary" : "outline"}
                        className={isActive ? "animate-pulse" : ""}
                      >
                        {isDone && <CheckCircle2 className="w-3 h-3 mr-1" />}
                        {step.label}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Add Smart Services</CardTitle>
            <CardDescription>
              Select which smart features to add to your cloned site
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {services.map((service) => {
                const Icon = service.icon;
                const isSelected = selectedServices.includes(service.id);
                return (
                  <div
                    key={service.id}
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      isSelected
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                    onClick={() => !isCloning && toggleService(service.id)}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => {}}
                        disabled={isCloning}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-primary" />
                          <span className="font-medium">{service.label}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {service.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {cloneStatus.status === "ready" && (
          <Card className="border-green-500/20 bg-green-500/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle2 className="w-5 h-5" />
                Your Site is Ready!
              </CardTitle>
              <CardDescription>
                Your website has been cloned and upgraded with smart features
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {assignedSpecialist && (
                <div className="flex items-center gap-3 p-4 bg-white rounded-lg border">
                  <span className="text-3xl">{assignedSpecialist.avatar}</span>
                  <div>
                    <p className="font-medium">{assignedSpecialist.name}</p>
                    <p className="text-sm text-muted-foreground">{assignedSpecialist.role} · Upgrade completed</p>
                  </div>
                </div>
              )}
              {cloneResult?.trainingData && (
                <div className="bg-muted rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Setup Complete</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Business context configured</li>
                    <li>• Chat widget customized for your content</li>
                    <li>• {cloneResult.trainingData.faqs.length} common questions ready</li>
                  </ul>
                </div>
              )}
              
              <div className="flex gap-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => window.open(cloneStatus.previewUrl, "_blank")}
                >
                  Preview Site
                </Button>
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={deploySite}
                >
                  Deploy to Production
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {cloneStatus.status === "deployed" && cloneStatus.deployedUrl && (
          <Card className="border-green-500/20 bg-green-500/5">
            <CardHeader>
              <CardTitle className="text-green-600">🎉 Site Deployed!</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-center mb-4">Your upgraded site is now live:</p>
              <a
                href={cloneStatus.deployedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center text-primary hover:underline text-lg"
              >
                {cloneStatus.deployedUrl}
              </a>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
